/**
 * seo.js — applies the head tags for the current route.
 *
 * scripts/prerender.js bakes these same tags into a static HTML file per route
 * at build time (that is what crawlers read). This hook is the runtime half:
 * it keeps <title>, the meta description and the canonical honest when the
 * user navigates client-side, where no new document is ever fetched.
 *
 * KEEP THE TWO IN STEP. A tag added here but not in prerender.js is right for
 * users and invisible to crawlers; the reverse is right for crawlers and stale
 * for users.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// seoConfig is CommonJS (it is shared with the Node prerender script), so it
// is imported as a default and destructured rather than via named imports.
import seoConfig from './seoConfig';

const { getRoute, canonicalFor, OG_IMAGE, SITE_NAME } = seoConfig;

/** Create the tag if it is missing, then set its content. */
function setMeta(selector, attrs, content) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Apply the head tags for the current pathname.
 *
 * @param {object} [overrides] optional per-render title/description overrides
 */
export function useSeo(overrides) {
  const { pathname } = useLocation();

  useEffect(() => {
    const route = getRoute(pathname);
    const title = (overrides && overrides.title) || route.title;
    const description = (overrides && overrides.description) || route.description;
    const canonical = canonicalFor(route.path);
    const robots = route.noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

    document.title = title;
    setMeta('meta[name="description"]', { name: 'description' }, description);
    setMeta('meta[name="robots"]', { name: 'robots' }, robots);

    setMeta('meta[property="og:title"]', { property: 'og:title' }, title);
    setMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    setMeta('meta[property="og:url"]', { property: 'og:url' }, canonical);
    setMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
    setMeta('meta[property="og:image"]', { property: 'og:image' }, OG_IMAGE);

    setMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, OG_IMAGE);

    // A canonical pointing at a noindex page would be contradictory; drop it.
    if (route.noindex) {
      const existing = document.head.querySelector('link[rel="canonical"]');
      if (existing) existing.remove();
    } else {
      setLink('canonical', canonical);
    }
    // Depend on the resolved values, not the `overrides` object identity.
  }, [pathname, overrides && overrides.title, overrides && overrides.description]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default useSeo;
